import { useSuspenseQuery } from '@apollo/client';
import React from 'react';
import { Link } from 'react-router-dom';

import { CopyTextButton } from '@eluve/blocks';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Heading,
  Stack,
} from '@eluve/components';

import { DeleteExperimentAction } from './DeleteExperimentAction';
import { ExperimentStatusToggle } from './ExperimentStatusToggle';
import { getExperimentsQuery } from './operations';

type ExperimentRow = {
  id: string;
  name: string;
  description: string | null;
  posthogFlagKey: string;
  isActive: boolean;
  firstActivatedAt: string | null;
  rank: number;
  mode: string;
  variantCount: number;
  createdAt: string;
  updatedAt: string;
};

const cols = new ColDefBuilder<ExperimentRow>()
  .defaultSortable('id', {
    cellRenderer: (row) => <CopyTextButton copyText={row.id} />,
  })
  .defaultSortable('name', {
    label: 'Name',
    cellRenderer: (row) => (
      <Link to={row.id} className="text-text-accent-secondary hover:underline">
        {row.name}
      </Link>
    ),
  })
  .defaultSortable('posthogFlagKey', 'PostHog Flag Key')
  .defaultSortable('isActive', {
    label: 'Status',
    cellRenderer: (row) => <ExperimentStatusToggle experimentId={row.id} />,
  })
  .defaultSortable('rank', 'Rank')
  .defaultSortable('mode', 'Mode')
  .defaultSortable('variantCount', 'Variants')
  .dateSortable('createdAt', 'Created At')
  .colDef({
    header: 'Actions',
    cell: ({ row }) => {
      const isActivated = row.original.firstActivatedAt !== null;
      return (
        <DeleteExperimentAction
          experimentId={row.original.id}
          experimentName={row.original.name}
          isActivated={isActivated}
        />
      );
    },
  })
  .build();

type ExperimentData = {
  id: string;
  name: string;
  description: string | null;
  posthogFlagKey: string;
  isActive: boolean;
  firstActivatedAt: string | null;
  rank: number;
  mode: string;
  createdAt: string;
  updatedAt: string;
  variants: Array<{
    id: string;
    name: string;
    posthogVariantKey: string;
    variantConfig: Record<string, unknown>;
    createdAt: string;
  }>;
};

export const ExperimentsListPage: React.FC = () => {
  const { data } = useSuspenseQuery(getExperimentsQuery);

  const experiments = (data?.experiments ?? []) as ExperimentData[];

  const rows: ExperimentRow[] = experiments.map((exp) => ({
    id: exp.id,
    name: exp.name,
    description: exp.description ?? null,
    posthogFlagKey: exp.posthogFlagKey,
    isActive: exp.isActive,
    firstActivatedAt: exp.firstActivatedAt ?? null,
    rank: exp.rank,
    mode: exp.mode ?? 'AB_TEST',
    variantCount: exp.variants?.length ?? 0,
    createdAt: exp.createdAt,
    updatedAt: exp.updatedAt,
  }));

  return (
    <Stack gap={2}>
      <Stack justify="between" gap={2} direction="horizontal">
        <div className="my-2 py-2">
          <Heading>Experiments</Heading>
        </div>
        <Link to="new">
          <Button>Create Experiment</Button>
        </Link>
      </Stack>
      <DataTable
        data={rows}
        columns={cols}
        enableGlobalSearch
        initialSortingState={[{ id: 'createdAt', desc: true }]}
      />
    </Stack>
  );
};
